import { AudioAnalyzerConstants } from "./configuration/AudioAnalyzerConstants";
import { getAudioBuffer } from "./helpers/AudioBufferProvider";

// 5 minutes per chunk.
const chunkDurationSec = 5 * 60;

/**
 * Analyze the audio file and calculate the cumulative max magnitudes for bass and wide frequencies.
 * The function takes an audio file or url as an argument and returns an object with properties bassMaxMagnitude and wideMaxMagnitude.
 * The cumulative max magnitudes are calculated by taking the top X percent of the max values from the audio analysis.
 * Processing is offloaded to a Web Worker to avoid overloading of the main thread.
 */
export async function analyzeCumulativeMaxMagnitudes(audio, onProgress, abortController) {
    let audioDecodingPercentage = 0;   // The percentage of the audio file that is decoding.
    let audioAnalysisPercentage = 0;   // The percentage of the audio file that is analyzing.
    let worker = undefined;            // The Web Worker that is processing the audio file.
    let audioBuffer = undefined;       // The audio buffer that contains the audio data.
 
    const terminate = () => {
        worker?.terminate();
        worker = undefined;
        audioBuffer = undefined;
    };

    abortController.signal.addEventListener('abort', () => terminate(), { once: true });

    return new Promise(async (resolve, reject) => {
        try {
            // Decode the audio file.
            audioBuffer = await getAudioBuffer(audio, (percentage) => {
                audioDecodingPercentage = percentage;
                onProgress?.(Math.round((audioDecodingPercentage + audioAnalysisPercentage) / 2));
            }, abortController);

            const samplesTotalCount = audioBuffer.getChannelData(0).length;
            const samplesAnalysisStep = Math.floor(audioBuffer.sampleRate * (1 / AudioAnalyzerConstants.MaxMagnitudeAnalysisFps));
            const samplesChunkLimit = Math.floor(chunkDurationSec * audioBuffer.sampleRate);
            
            // Create a new Web Worker for audio processing
            worker = new Worker(new URL('./MaxMagnitudeWorker.js', import.meta.url), { type: 'module' });
            worker.onerror = (error) => {
                terminate();
                reject(error);
            };

            // Set up message handler for worker responses
            worker.onmessage = async (event) => {
                try {
                    // Handle the cancellation of the audio analysis.
                    if (abortController.signal.aborted) {
                        terminate();
                        return reject('The max magnitudes analysis has been aborted.');
                    }

                    const { operation } = event.data;
                    switch (operation) {
                        case 'waiting':
                            // When the worker is ready and waiting to process the next audio chunk.
                            const { lastProcessedSample } = event.data;
                            let isLastChunk = false;

                            let startSample = Math.floor(lastProcessedSample == 0 ? 0 : lastProcessedSample + samplesAnalysisStep);
                            let endSample = Math.floor(startSample + samplesChunkLimit);

                            if (endSample >= samplesTotalCount) {
                                endSample = samplesTotalCount - 1;
                                isLastChunk = true;
                            }

                            // Slice the each channel data for the current chunk.
                            const channelsData = [];
                            for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
                                const data = audioBuffer.getChannelData(i).slice(startSample, endSample);
                                channelsData.push(data);
                            }

                            // Send the chunk to the worker for analysis.
                            console.log(`Start of chunk analysis from ${startSample} to ${endSample} samples...`);
                            worker.postMessage({
                                operation: 'analyze',
                                channelsData: channelsData,
                                chunkStartSample: startSample,
                                isLastChunk: isLastChunk
                            });
                            break;
                        case 'progress':
                            // When the worker is processing and the new progress phase is available.
                            audioAnalysisPercentage = event.data.percentage;
                            onProgress?.(Math.round((audioDecodingPercentage + audioAnalysisPercentage) / 2));
                            break;
                        case 'complete':
                            // When the worker has completed the analysis and the max magnitudes are available.
                            terminate();
                            resolve(event.data.magnitudes);
                            break;
                    }

                    // Allow the main thread to update the UI.
                    await new Promise(resolve => setTimeout(resolve, 0));
                } catch (error) {
                    terminate();
                    reject(error);
                }
            };

            console.log(`Start of CMM analysis for ${audioBuffer.duration}s audio with ${audioBuffer.sampleRate} sample rate.`);
            console.log(`${samplesTotalCount} samples received after audio decoding.`);

            // Send the general audio information to the worker.
            worker.postMessage({
                operation: 'initialize',
                samplesTotalCount: samplesTotalCount,
                samplesAnalysisStep: samplesAnalysisStep
            });
        } catch (error) {
            terminate();
            reject(error);
        }
    });
}